import { api } from '../../client';

export interface CompanySettingsDto {
  id?: string;
  company_name: string;
  trading_name?: string;
  industry?: string;
  currency?: string;
  tax_id?: string;
  registration_number?: string;
  email?: string;
  phone?: string;
  website?: string;
  street_address?: string;
  city?: string;
  state_province?: string;
  postal_code?: string;
  country?: string;
  updated_at?: string;
}

const STORAGE_KEY = 'spynerp_company_settings';

/** Default company values for demo/local use when API is not available */
const defaultCompany: CompanySettingsDto = {
  company_name: 'SpynERP Demo Company',
  trading_name: 'SpynERP Demo',
  industry: 'General Business',
  currency: 'USD - US Dollar',
  tax_id: '12-3456789',
  registration_number: '',
  email: 'contact@spynerp-demo.com',
  phone: '+1 (555) 123-4567',
  website: 'https://company.com',
  street_address: '123 Business Avenue',
  city: 'New York',
  state_province: 'NY',
  postal_code: '10001',
  country: 'United States',
};

function loadFromStorage(): CompanySettingsDto {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as CompanySettingsDto;
      return { ...defaultCompany, ...parsed };
    }
  } catch {
    // ignore
  }
  return { ...defaultCompany };
}

function saveToStorage(data: CompanySettingsDto): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

export const settingsCompanyApi = {
  async get(): Promise<{ data?: CompanySettingsDto; message?: string }> {
    try {
      const res = await api.get('/settings/company') as { data?: CompanySettingsDto };
      if (res?.data) return res;
    } catch {
      // Fallback to localStorage when backend not available
    }
    return { data: loadFromStorage() };
  },

  async update(
    data: Partial<CompanySettingsDto>
  ): Promise<{ data?: CompanySettingsDto; message?: string; errors?: Record<string, string[]> }> {
    try {
      const res = await api.put('/settings/company', data) as {
        data?: CompanySettingsDto;
        message?: string;
        errors?: Record<string, string[]>;
      };
      if (res?.data) {
        saveToStorage(res.data);
        return res;
      }
    } catch {
      // Fallback: merge and persist locally
    }
    const current = loadFromStorage();
    const updated = { ...current, ...data } as CompanySettingsDto;
    saveToStorage(updated);
    return { data: updated, message: 'Saved locally.' };
  },
};
